import { Copy, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { FamilySignupCode } from '../../lib/types';
import { useParent } from '../ParentContext';

interface Code {
  id: string;
  code: string;
  note: string;
  percentOff: number;
  months: number;
  founder: boolean;
  maxUses: number | null;
  endsAt: number;
  active: boolean;
  uses: number;
}

const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const isoDay = (ms: number) => (ms ? new Date(ms).toISOString().slice(0, 10) : '');
const discountText = (c: { founder: boolean; percentOff: number; months: number }, founderPct?: number) =>
  c.founder ? `Founder price${founderPct ? `: ${founderPct}% off` : ''}, forever` : `${c.percentOff}% off ${c.months ? `for ${c.months} month${c.months === 1 ? '' : 's'}` : 'forever'}`;
const blank = { code: '', note: '', percentOff: 20, months: 3, forever: false, founder: false, maxUses: '', endsAt: '' };

/**
 * Admin → Server settings → Sign-up codes (#879): codes families enter at sign-up for a discount (a % off for some
 * months, or forever), or to become Founders. Limits and end dates; turning a code off never touches families who used it.
 */
export function SignupCodes({ founderPercent }: { founderPercent: number }) {
  const { toast } = useParent();
  const [codes, setCodes] = useState<Code[] | null>(null);
  const [form, setForm] = useState<typeof blank | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ codes: Code[] }>('/api/admin/signup-codes').then((r) => setCodes(r.codes), (e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  const status = (c: Code) =>
    !c.active ? ['Off', ''] : c.endsAt && c.endsAt < Date.now() ? ['Ended', ''] : c.maxUses !== null && c.uses >= c.maxUses ? ['Used up', 'warn'] : ['On', 'good'];
  const link = (c: Code) => `${window.location.origin}/signup?ref=${c.code}`;
  async function save() {
    if (!form) return;
    const body = {
      code: form.code,
      note: form.note,
      percentOff: form.founder ? 0 : Number(form.percentOff) || 0,
      months: form.founder || form.forever ? 0 : Math.max(1, Number(form.months) || 1),
      founder: form.founder,
      maxUses: form.maxUses.trim() ? Math.max(1, parseInt(form.maxUses, 10) || 1) : null,
      endsAt: form.endsAt ? new Date(`${form.endsAt}T23:59:59`).getTime() : 0,
      active: editing ? (codes?.find((c) => c.id === editing)?.active ?? true) : true,
    };
    try {
      const r = editing ? await api.put<{ codes: Code[] }>(`/api/admin/signup-codes/${editing}`, body) : await api.post<{ codes: Code[] }>('/api/admin/signup-codes', body);
      setCodes(r.codes);
      setForm(null);
      setEditing(null);
      toast({ text: `${body.code.toUpperCase()} saved`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  async function toggle(c: Code) {
    try {
      const r = await api.put<{ codes: Code[] }>(`/api/admin/signup-codes/${c.id}`, { ...c, active: !c.active });
      setCodes(r.codes);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  function copy(c: Code) {
    navigator.clipboard.writeText(link(c)).then(
      () => toast({ text: 'Link copied', tone: 'good' }),
      () => toast({ text: link(c), tone: 'info' }),
    );
  }
  if (!codes) return <p className="muted small">Loading…</p>;
  return (
    <div className="stack">
      <p className="muted small">
        Codes families enter when they sign up, or a link with the code in it. Each gives its own discount at checkout; a Founder code makes the family a
        Founder instead ({founderPercent}% off, forever, set in Payments). Editing or turning off a code never changes families who already used it.
      </p>
      {codes.length > 0 && (
        <div className="table-scroll">
          <table className="bn-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Used</th>
                <th>Ends</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const [label, tone] = status(c);
                return (
                  <tr key={c.id}>
                    <td>
                      <code className="partner-code">{c.code}</code>
                      {c.note && <span className="muted small block">{c.note}</span>}
                    </td>
                    <td>
                      {discountText(c, founderPercent)}
                      {c.founder && <span className="tag good"> Founder</span>}
                    </td>
                    <td className="num">{c.maxUses === null ? `${c.uses} · no limit` : `${c.uses} of ${c.maxUses}`}</td>
                    <td>{c.endsAt ? day(c.endsAt) : '—'}</td>
                    <td>
                      <span className={`tag ${tone}`}>{label}</span>
                    </td>
                    <td className="row-gap nowrap">
                      <button type="button" className="btn small" onClick={() => copy(c)} aria-label={`Copy the ${c.code} link`}>
                        <Copy size={14} /> Link
                      </button>
                      <button
                        type="button"
                        className="btn small"
                        onClick={() => {
                          setEditing(c.id);
                          setForm({ code: c.code, note: c.note, percentOff: c.percentOff || 20, months: c.months || 3, forever: !c.founder && c.months === 0, founder: c.founder, maxUses: c.maxUses === null ? '' : String(c.maxUses), endsAt: isoDay(c.endsAt) });
                        }}
                      >
                        Edit
                      </button>
                      <button type="button" className="btn small" onClick={() => toggle(c)}>
                        {c.active ? 'Turn off' : 'Turn on'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {form ? (
        <div className="stack signup-code-form">
          <h4>{editing ? `Edit ${form.code}` : 'New code'}</h4>
          <div className="field-row">
            <label>
              Code
              <input value={form.code} maxLength={16} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="FAIR2026" />
            </label>
            <label>
              Note <span className="muted small">(for you)</span>
              <input value={form.note} maxLength={120} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Homeschool fair" />
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={form.founder} onChange={(e) => setForm({ ...form, founder: e.target.checked })} />
            <span>
              <strong>Founder code</strong>
              <span className="muted small block">Families who use it become Founders and get the Founder price ({founderPercent}% off, forever) instead of a discount set here.</span>
            </span>
          </label>
          {!form.founder && (
            <div className="field-row">
              <label>
                Percent off
                <input type="number" min={1} max={100} value={form.percentOff} onChange={(e) => setForm({ ...form, percentOff: Number(e.target.value) })} />
              </label>
              <label>
                For (months)
                <input type="number" min={1} max={60} value={form.months} disabled={form.forever} onChange={(e) => setForm({ ...form, months: Number(e.target.value) })} />
              </label>
              <label className="check signup-code-forever">
                <input type="checkbox" checked={form.forever} onChange={(e) => setForm({ ...form, forever: e.target.checked })} /> Forever
              </label>
            </div>
          )}
          <div className="field-row">
            <label>
              How many families <span className="muted small">(blank = no limit)</span>
              <input type="number" min={1} value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: e.target.value })} />
            </label>
            <label>
              Ends <span className="muted small">(blank = no end)</span>
              <input type="date" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
            </label>
          </div>
          <div className="row-gap">
            <button type="button" className="btn primary" onClick={save} disabled={form.code.trim().length < 3}>
              {editing ? 'Save code' : 'Add code'}
            </button>
            <button type="button" className="btn" onClick={() => (setForm(null), setEditing(null))}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn" onClick={() => setForm({ ...blank })}>
          <Plus size={16} /> New code
        </button>
      )}
    </div>
  );
}

/** On a family in Admin → Families (#879): the sign-up code it used and whether its discount is on. */
export function FamilySignupCodeLine({ familyId, c, onChanged }: { familyId: string; c: FamilySignupCode; onChanged: () => void }) {
  const { toast } = useParent();
  const [busy, setBusy] = useState(false);
  const [label, tone, note] =
    c.status === 'applied'
      ? ['Discount applied', 'good', c.endsAt ? `until ${day(c.endsAt)}` : 'forever']
      : c.status === 'used'
        ? ['Discount used', 'good', 'ran its months; full price now']
        : c.status === 'not-subscribed'
          ? ['Not subscribed yet', '', 'goes on at checkout']
          : ['Not applied', 'warn', ''];
  async function apply() {
    setBusy(true);
    try {
      await api.post(`/api/admin/families/${familyId}/signup-code-discount`);
      toast({ text: `${c.code} discount is on the subscription now`, tone: 'good' });
      onChanged();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="amb-family">
      <span>
        Joined with code <code className="partner-code">{c.code}</code>{' '}
        <span className="muted small">
          {c.note ? `(${c.note}) ` : ''}on {day(c.usedAt)} · {discountText(c)}
        </span>
      </span>
      <span className="amb-status">
        <span className={`tag ${tone}`}>{label}</span>
        {note && <span className="muted small amb-note">{note}</span>}
      </span>
      {c.status === 'not-applied' && (
        <button type="button" className="btn small" onClick={apply} disabled={busy}>
          {busy ? 'Applying…' : 'Apply the discount'}
        </button>
      )}
    </div>
  );
}
